import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLeadBalance } from '@/lib/creditsBus';
import { CONTRACTOR_TABS } from '@/data/contractorNav';

/**
 * Contractor workspace tabs — Credits · My Leads · Find Leads · Membership ·
 * Profile. Phones (most customers): a 5-column grid with icon + short label,
 * so every tab is visible without scrolling sideways. Desktop: one row with
 * full labels. Lead count and credit balance appear as badges.
 */
const ContractorNav: React.FC<{ leadCount?: number }> = ({ leadCount }) => {
  const { path, lang } = useI18n();
  const { profile } = useAuth();
  const { pathname, search } = useLocation();
  const { balance } = useLeadBalance(profile?.id);
  const es = lang === 'es';
  const onCredits = /tab=credits/.test(search);

  return (
    <nav aria-label={es ? 'Herramientas del contratista' : 'Contractor tools'} className="mb-6">
      <div className="grid grid-cols-5 gap-1 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm sm:flex sm:gap-1.5 sm:p-1.5">
        {CONTRACTOR_TABS.map((item) => {
          const href = path(item.to);
          const base = href.split('?')[0];
          const onBase = pathname === base || pathname === `${base}/`;
          const active = item.key === 'credits' ? onBase && onCredits : onBase && !(item.key === 'membership' && onCredits);
          const badge =
            item.key === 'leads' && typeof leadCount === 'number'
              ? leadCount
              : item.key === 'credits'
              ? balance.spendable
              : null;
          const Icon = item.icon;
          const highlight = item.key === 'leads' || item.key === 'credits';
          return (
            <Link
              key={item.key}
              to={href}
              aria-current={active ? 'page' : undefined}
              className={`relative flex min-h-[56px] flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[11px] font-bold leading-tight transition-colors sm:min-h-[44px] sm:flex-row sm:gap-2 sm:px-3.5 sm:text-sm ${
                active
                  ? 'bg-slate-900 text-white'
                  : highlight
                  ? 'text-orange-700 hover:bg-orange-50'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <Icon className="h-5 w-5 sm:h-4 sm:w-4" />
              <span className="sm:hidden">{es ? item.shortEs : item.shortEn}</span>
              <span className="hidden sm:inline">{es ? item.es : item.en}</span>
              {badge !== null && (
                <span
                  className={`absolute right-1 top-1 min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10px] tabular-nums sm:static sm:text-[11px] ${
                    active ? 'bg-white/20 text-white' : 'bg-orange-500 text-white'
                  }`}
                >
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default ContractorNav;
